import type { PlayerInput } from '../types';

/** Button bitfield (§4.2). u32 since D45 (bits 0..15 were fully assigned; TRAP needed a wider field). */
export const BTN = {
  SPRINT: 1 << 0,
  PASS: 1 << 1,
  THROUGH: 1 << 2,
  SHOOT: 1 << 3,
  TACKLE: 1 << 4,
  SLIDE: 1 << 5,
  SHOVE: 1 << 6,
  PUNCH: 1 << 7,
  DIVE: 1 << 8,
  SHIELD: 1 << 9,
  CONTAIN: 1 << 10, // D40 defensive stance (hold): no sprint, quick strafe, wider blocking body. (Bit reassigned from the never-consumed HEADER placeholder — headers re-bit at implementation.)
  CALL: 1 << 11,
  GK_DIVE_L: 1 << 12,
  GK_DIVE_R: 1 << 13,
  SHOUT: 1 << 14, // hold: doubles voice radius, sets PlayerState.shouting
  SYNTHETIC: 1 << 15, // SERVER-SET ONLY: this frame was substituted (§5.4c). InputHub strips it from client frames.
  TRAP: 1 << 16, // D45 Item 6: catch/trap a loose ball (E) — needs the u32 field (bits 0..15 were full)
  /** D110 (HB-A/HB-P1): the LOFT hold-modifier (V). Not a verb — alone it does nothing. Held when a PASS
   *  or THROUGH fires, it lofts that kick (chip / cross). CONTAIN took the original HEADER bit 10 in D40
   *  ("headers re-bit at implementation") and the aerial finish rides SHOOT instead (HB-P2), so bit 17 is
   *  the only new bit this wave needs. NOT a kick bit: the D58 kick strip and sessionKickLatch ignore it,
   *  so a held V deliberately survives a follow-switch. */
  LOFT: 1 << 17,
  /** D137 (§7.3): the self-LOB — "single press of the button without an action just lofts the ball up.
   *  useful to play over others. quick double tap lofts higher" (user). A one-frame pulse the client
   *  synthesises from a tap of the LOFT key; the sim knocks the carried ball up at the `kick.LOB` rung
   *  chosen by whether BTN.LOFT rides along (tap vs double-tap). Not a kick VERB — `pickKickButton` never
   *  returns it, so it cannot win the SHOOT > THROUGH > PASS priority, and the carrier keeps running
   *  through the release (PT4-P20). Bit 18: the PackedInput layout is unchanged, but a pre-D137 server
   *  masks it off and silently swallows every lob, so client and server deploy together (D110 house rule). */
  LOB: 1 << 18,
} as const;

/** A zeroed input carrying the SYNTHETIC bit (§4.2) — used for vanished/frozen actors. */
export const NULL_INPUT: PlayerInput = Object.freeze({
  moveX: 0,
  moveY: 0,
  aimYaw: 0,
  aimElev: 0,
  buttons: BTN.SYNTHETIC,
  emote: 0,
});
